import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import logger from 'src/utils/logger';
import Modal from 'src/components/layouts/Modal/Modal';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { CustomerForecastPromotion, useRequestPromotionChange } from 'src/services/forecastPromotions';

interface Props {
  promotion: CustomerForecastPromotion;
  isOpen: boolean;
  onClose: () => void;
}

const MAX_REASON_LENGTH = 2000;

const RequestPromotionDeleteModal: React.FC<Props> = ({ promotion, isOpen, onClose }) => {
  const { t } = useTranslation();
  const mutation = useRequestPromotionChange();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  const tooLong = reason.length > MAX_REASON_LENGTH;
  const submitDisabled = tooLong || mutation.isPending;

  const handleSubmit = async () => {
    try {
      await mutation.mutateAsync({
        id: promotion.id,
        payload: { request_type: 'delete', reason: reason.trim() || null },
      });
      toast.success(t('forecastPromotions.requestDeleteSuccess'));
      onClose();
    } catch (error) {
      logger.error('Failed to request promotion deletion', error);
      toast.error(t('forecastPromotions.requestDeleteFailed'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} smallSize>
      <div className="max-w-[480px] w-full">
        <div className="p-32 flex flex-col gap-16">
          <h4 className="typo-title-24">{t('forecastPromotions.requestDeleteTitle')}</h4>
          <p className="typo-text-14 text-faded">{t('forecastPromotions.requestDeleteDescription')}</p>
          <Textarea
            label={t('forecastPromotions.reasonLabel')}
            placeholder={t('forecastPromotions.reasonPlaceholder')}
            value={reason}
            onChange={e => setReason(e.target.value)}
            error={tooLong}
            errorMessage={t('forecastPromotions.reasonTooLong', { max: MAX_REASON_LENGTH })}
          />
        </div>
        <div className="border-subtle-top py-16 px-24 flex justify-end gap-12">
          <Button
            tone={ButtonToneProps.SECONDARY}
            title={t('forecastPromotions.cancel')}
            onClick={onClose}
            disabled={mutation.isPending}
          />
          <Button
            tone={ButtonToneProps.DESTRUCTIVE}
            title={t('forecastPromotions.requestDeleteSubmit')}
            onClick={handleSubmit}
            disabled={submitDisabled}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequestPromotionDeleteModal;
